import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CreationMode } from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { AzureOauthUrl } from '@models/azure-oauth.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, CardGroupComponent, InputLabelComponent, LoaderComponent, SelectComponent } from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import { AzureVMSubscription } from 'mbs-ui-shared@models/StorageAccount';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { finalize } from 'rxjs/operators';
import { e2eLocators } from '../credential-forms.constants';
import { AzureVMCredentialForm } from '../credential-forms.model';

@UntilDestroy()
@Component({
  selector: 'mbs-credential-form-azure-vm',
  templateUrl: './credential-form-azure-vm.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:message)': 'onWindowMessage($event)'
  },
  imports: [
    ButtonComponent,
    CardGroupComponent,
    CommonModule,
    I18NextPipe,
    InputLabelComponent,
    LoaderComponent,
    ReactiveFormsModule,
    SelectComponent
  ]
})
export class CredentialFormAzureVmComponent implements OnInit {
  private storageAccountsService = inject(StorageAccountsService);

  @Input() form: AzureVMCredentialForm;
  @Input() mode: CreationMode;

  public readonly StorageType = StorageType;
  public readonly creationMode = CreationMode;
  public readonly e2eLocators = e2eLocators;

  public loading = signal(false);
  public isLogged = signal(false);
  public subscriptions: AzureVMSubscription[];

  ngOnInit() {
    if (this.mode === CreationMode.Edit) {
      const { value } = this.form;

      const subscription = {
        subscriptionDisplayName: value?.subscriptionDisplayName,
        subscriptionId: value?.subscriptionId,
        tenantId: value?.tenantId
      };

      this.subscriptions = [subscription];
      this.form.patchValue({
        ...this.form.value,
        ...subscription
      });
    }

    this.subscriptionDisplayName.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      const subscription = this.subscriptions.find((subscription) => subscription.subscriptionDisplayName === val);

      this.form.patchValue(
        {
          ...this.form.value,
          ...subscription
        },
        { emitEvent: false }
      );
    });
  }

  get subscriptionDisplayName(): FormControl {
    return this.form.get('subscriptionDisplayName') as FormControl;
  }

  public logIn(): void {
    window.open(
      `${AzureOauthUrl}?action=add-storage-account`,
      'Azure OAuth 2.0',
      'width=500,height=500,toolbar=0,menubar=0,location=0,status=1,scrollbars=1,resizable=1,left=0,top=0'
    );
  }

  public onWindowMessage(event: MessageEvent) {
    const { msg, data: credential } = event.data;

    if (msg === 'add-storage-account') {
      this.applyAction(credential);
    }
  }

  public applyAction({ code }: { code: string }) {
    this.loading.set(true);

    this.storageAccountsService
      .getAzureVMSubscriptions(code, `${location.origin}${AzureOauthUrl}`)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe(({ subscriptions, refreshToken }) => {
        this.form.patchValue(
          {
            ...this.form.value,
            refreshToken,
            subscriptionDisplayName: null,
            subscriptionId: null,
            tenantId: null
          },
          { emitEvent: false }
        );
        this.isLogged.set(true);
        this.subscriptions = subscriptions;
      });
  }
}
